import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Download, FileText, Image as ImageIcon, Film, Music, File, Loader2, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '../ui/button';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const getFileUrl = (fileId) =>
  `${BACKEND}/api/uploads/${fileId}?token=${encodeURIComponent(localStorage.getItem('ems_token') || '')}`;

const isImage = (name) => /\.(jpg|jpeg|png|gif|bmp|svg|webp)$/i.test(name || '');
const isPDF = (name) => /\.pdf$/i.test(name || '');
const isVideo = (name) => /\.(mp4|mov|avi|wmv|webm)$/i.test(name || '');
const isAudio = (name) => /\.(mp3|wav|ogg|aac|flac)$/i.test(name || '');
const isDocx = (name) => /\.(docx|doc)$/i.test(name || '');

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

function useDocxHtml(fileId, fileName) {
  const [html, setHtml] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    setHtml(null);
    if (!fileId || !fileName || !isDocx(fileName)) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${BACKEND}/api/uploads/${fileId}/preview`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('ems_token') || ''}` },
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.text(); })
      .then(text => { if (!cancelled) setHtml(text); })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [fileId, fileName]);

  return { html, loading, error };
}

function PDFViewer({ fileId }) {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const [pdfDoc, setPdfDoc] = useState(null);
  const [pageNum, setPageNum] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!fileId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPdfDoc(null);
    setPageNum(1);

    const token = localStorage.getItem('ems_token') || '';
    fetch(`${BACKEND}/api/uploads/${fileId}?inline=true`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => { if (!res.ok) throw new Error(`HTTP ${res.status}`); return res.arrayBuffer(); })
      .then(data => {
        if (cancelled) return;
        return pdfjsLib.getDocument({ data }).promise;
      })
      .then(pdf => {
        if (cancelled || !pdf) return;
        setPdfDoc(pdf);
        setNumPages(pdf.numPages);
        setLoading(false);
      })
      .catch(err => {
        if (!cancelled) { setError(err.message); setLoading(false); }
      });

    return () => { cancelled = true; };
  }, [fileId]);

  const renderPage = useCallback(async (num) => {
    if (!pdfDoc || !canvasRef.current || !containerRef.current) return;
    try {
      const page = await pdfDoc.getPage(num);
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 16;
      const unscaledViewport = page.getViewport({ scale: 1 });
      const scale = containerWidth / unscaledViewport.width;
      const viewport = page.getViewport({ scale });

      const canvas = canvasRef.current;
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      await page.render({ canvasContext: canvas.getContext('2d'), viewport }).promise;
    } catch (e) {
      console.error('PDF render error:', e);
    }
  }, [pdfDoc]);

  useEffect(() => { renderPage(pageNum); }, [pageNum, renderPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading PDF...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
        <FileText className="w-12 h-12 opacity-30" />
        <p className="text-sm">Could not load PDF preview</p>
        <Button variant="outline" size="sm" onClick={() => window.open(getFileUrl(fileId), '_blank')} data-testid="doc-preview-open-pdf">
          <Download className="w-4 h-4 mr-2" /> Open in New Tab
        </Button>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="flex flex-col h-full" data-testid="pdf-viewer">
      {numPages > 1 && (
        <div className="flex items-center justify-center gap-2 py-1.5 border-b bg-muted/30 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={pageNum <= 1} onClick={() => setPageNum(p => p - 1)} data-testid="pdf-prev-page">
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <span className="text-xs text-muted-foreground" data-testid="pdf-page-info">{pageNum} / {numPages}</span>
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" disabled={pageNum >= numPages} onClick={() => setPageNum(p => p + 1)} data-testid="pdf-next-page">
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
      )}
      <div className="flex-1 overflow-auto flex justify-center p-2">
        <canvas ref={canvasRef} className="max-w-full" data-testid="pdf-canvas" />
      </div>
    </div>
  );
}

export default function DocumentPreview({ file, onClose }) {
  const { html: docxHtml, loading: docxLoading, error: docxError } = useDocxHtml(file?.file_id, file?.filename);

  if (!file) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm" data-testid="doc-preview-empty">
        <div className="text-center space-y-2">
          <FileText className="w-12 h-12 mx-auto opacity-30" />
          <p>Select a file to preview</p>
        </div>
      </div>
    );
  }

  const url = getFileUrl(file.file_id);
  const name = file.filename || '';

  return (
    <div className="h-full flex flex-col bg-card rounded-lg border" data-testid="doc-preview-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b bg-muted/50 rounded-t-lg">
        <div className="flex items-center gap-2 min-w-0">
          {isImage(name) ? <ImageIcon className="w-4 h-4 text-blue-500 shrink-0" /> :
           isPDF(name) ? <FileText className="w-4 h-4 text-red-500 shrink-0" /> :
           isVideo(name) ? <Film className="w-4 h-4 text-purple-500 shrink-0" /> :
           isAudio(name) ? <Music className="w-4 h-4 text-green-500 shrink-0" /> :
           <File className="w-4 h-4 text-muted-foreground shrink-0" />}
          <span className="text-sm font-medium truncate">{name}</span>
          {file.size && <span className="text-xs text-muted-foreground shrink-0">({formatSize(file.size)})</span>}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => window.open(url, '_blank')} data-testid="doc-preview-download">
            <Download className="w-3.5 h-3.5" />
          </Button>
          {onClose && (
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={onClose} data-testid="doc-preview-close">
              <X className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-2" data-testid="doc-preview-content">
        {isImage(name) ? (
          <div className="flex items-center justify-center h-full">
            <img src={url} alt={name} className="max-w-full max-h-full object-contain rounded" loading="lazy" data-testid="doc-preview-image" />
          </div>
        ) : isPDF(name) ? (
          <PDFViewer fileId={file.file_id} />
        ) : isDocx(name) ? (
          docxLoading ? (
            <div className="flex items-center justify-center h-full text-muted-foreground">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Converting document...
            </div>
          ) : docxError ? (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-3">
              <FileText className="w-12 h-12 opacity-30" />
              <p className="text-sm">Could not preview document</p>
              <Button variant="outline" size="sm" onClick={() => window.open(url, '_blank')} data-testid="doc-preview-open-docx">
                <Download className="w-4 h-4 mr-2" /> Download
              </Button>
            </div>
          ) : docxHtml ? (
            <iframe
              srcDoc={docxHtml}
              title={name}
              className="w-full h-full rounded border-0 bg-white"
              sandbox="allow-same-origin"
              data-testid="doc-preview-docx-iframe"
            />
          ) : null
        ) : isVideo(name) ? (
          <div className="flex items-center justify-center h-full">
            <video controls className="max-w-full max-h-full rounded" src={url} data-testid="doc-preview-video" />
          </div>
        ) : isAudio(name) ? (
          <div className="flex items-center justify-center h-full">
            <audio controls src={url} className="w-full max-w-md" data-testid="doc-preview-audio" />
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground gap-4">
            <File className="w-16 h-16 opacity-30" />
            <p className="text-sm">Preview not available for this file type</p>
            <Button variant="outline" onClick={() => window.open(url, '_blank')} data-testid="doc-preview-open-file">
              <Download className="w-4 h-4 mr-2" /> Open / Download
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
